import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { RoleViewStore } from "../../store";
import { RoleView } from "../view/RoleView";
import { RoleUpdateStore } from "../../store";
import { RoleUpdate } from "../update/RoleUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type RoleModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const RoleModalContent: React.FC<RoleModalContentProps> = observer(
  ({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new RoleUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new RoleViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <RoleView roleViewStore={storeInfo.store as RoleViewStore} />
        )}
        {storeInfo.type === "update" && (
          <RoleUpdate
            roleUpdateStore={storeInfo.store as RoleUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  }
);
